import { z } from 'zod';

/**
 * Notification panel contracts (M8 — PM-2050). The Notification entity is the
 * persistent record behind every conflict and system event raised by the
 * ResourceAxis write paths.
 *
 * @provisional until M10 — the panel UI (PM-2054) may reshape the list
 * response (grouping, counts per context) before the contract freezes.
 *
 * Design notes:
 * - `notificationType`, `contextType` are open strings (ADR 0023 pattern —
 *   the sets are declared extensible, never closed enums).
 * - `isRead` and `isResolved` are independent: reading is not acknowledging.
 * - `payload` is the type-specific document; the panel renders it without a
 *   second query (PM-2053). `payload.detail` carries entity references
 *   ({ workItemId | allocationId }) — the UI resolves them to links; the API
 *   never builds URLs.
 */

export const NotificationSeveritySchema = z.enum(['INFO', 'WARNING', 'ERROR']);
export type NotificationSeverity = z.infer<typeof NotificationSeveritySchema>;

/**
 * ACTIONABLE = an open conflict with identity (tenant, type, context): it is
 * upserted while it holds and auto-resolved by the system when the condition
 * clears (PM-2051/2052) — never by user acknowledgement.
 * TRANSIENT = a point-in-time event: inserted per occurrence, resolved only
 * by explicit user acknowledgement.
 */
export const NotificationLifecycleSchema = z.enum(['ACTIONABLE', 'TRANSIENT']);
export type NotificationLifecycle = z.infer<typeof NotificationLifecycleSchema>;

export const NotificationResponseSchema = z.object({
  id: z.string().uuid(),
  notificationType: z.string(),
  severity: NotificationSeveritySchema,
  lifecycle: NotificationLifecycleSchema,
  isRead: z.boolean(),
  isResolved: z.boolean(),
  resolvedAt: z.string().nullable(),
  contextType: z.string(),
  contextId: z.string().uuid(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NotificationResponse = z.infer<typeof NotificationResponseSchema>;

export const NotificationListQuerySchema = z.object({
  /** Same coercion idiom as `includeRetired` — pass `true`/omit. */
  unreadOnly: z.coerce.boolean().optional().default(false),
  unresolvedOnly: z.coerce.boolean().optional().default(false),
  contextType: z.string().min(1).optional(),
  contextId: z.string().uuid().optional(),
  type: z.string().min(1).optional(),
  /** Opaque — echo `nextCursor` back verbatim. */
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

export const NotificationListResponseSchema = z.object({
  notifications: z.array(NotificationResponseSchema),
  /** Present while more pages exist (created_at desc, id tiebreak). */
  nextCursor: z.string().nullable(),
  /** Unread notifications of the tenant, independent of the filters. */
  unreadCount: z.number().int(),
});
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

export const NotificationIdParamsSchema = z.object({ id: z.string().uuid() });
export type NotificationIdParams = z.infer<typeof NotificationIdParamsSchema>;
